// src/components/AuthInitializer.tsx
import { useAuthStore } from '@shared/stores/authStore';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuthData } from '../hooks/useAuth';

const AuthInitializer: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { t } = useTranslation('auth');
  const { token, user, isLoadingUser, refetchUser, currentUser, userError } = useAuthData();

  const { role, team, logout } = useAuthStore();
  const hasTriedToFetch = useRef(false);
  const [loadingTimeout, setLoadingTimeout] = useState(false);

  /** Overlay mientras /me completa role/team: NO desmontar AppRoutes (evita removeChild / conflictos con React Router y portales). */
  const showProfileLoadingOverlay = Boolean(token) && (!role || !team) && !userError && !loadingTimeout;

  useEffect(() => {
    // ✅ Solo iniciar carga si hay token pero no hay usuario
    if (token && !user && !currentUser && !isLoadingUser && !hasTriedToFetch.current) {
      hasTriedToFetch.current = true;
      refetchUser();
    }

    // ✅ Reset al cambiar token
    if (!token) {
      hasTriedToFetch.current = false;
      setLoadingTimeout(false);
    }
  }, [token, user, currentUser, isLoadingUser, refetchUser]);

  // 🎯 Timeout para evitar quedar atascado en loading
  useEffect(() => {
    if (token && (!role || !team) && !isLoadingUser) {
      const timer = setTimeout(() => {
        setLoadingTimeout(true);
      }, 10000); // 10 segundos

      return () => clearTimeout(timer);
    }
  }, [token, role, team, isLoadingUser]);

  // 🎯 Token sin perfil completo: error/timeout → pantalla de recuperación (sin app debajo)
  if (token && (!role || !team) && (userError || loadingTimeout)) {
    return (
      <div className="flex items-center justify-center h-screen bg-background">
        <div className="flex flex-col items-center gap-4 max-w-md text-center">
          <div className="text-destructive text-4xl">⚠️</div>
          <p className="text-lg font-semibold">{t('errors.loadUserDataTitle')}</p>
          <p className="text-sm text-muted-foreground">{t('errors.loadUserDataDescription')}</p>
          <button
            onClick={() => logout()}
            className="mt-4 px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors"
          >
            {t('errors.retryLogin')}
          </button>
        </div>
      </div>
    );
  }

  // ✅ App siempre montada; durante carga de perfil solo se superpone overlay (no desmontar Router)
  return (
    <>
      {showProfileLoadingOverlay && (
        <div
          className="fixed inset-0 z-[99999] flex items-center justify-center bg-background"
          aria-live="polite"
          aria-busy="true"
        >
          <div className="flex flex-col items-center gap-4">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary" />
            <p className="text-sm text-muted-foreground">{t('login.loading')}</p>
          </div>
        </div>
      )}
      {children}
    </>
  );
};

export default AuthInitializer;
